// CompetitionRatingBoard — the Stats section's head piece (UI-SPEC §9.1,
// K9 item 3 mobile). Data-permitting: the PARENT renders it only when a
// season rating source exists (K4); this component additionally returns null
// without rows ("empty renders nothing").
//
// Chip colors come from the platform's SINGLE tier source —
// ratingTier10() in components/totw/totwUtils.js — never re-implemented.
// The displayed value is the BE-computed per-match average (rating/court/100),
// shown with two decimals ("8.34", UI-SPEC §22).
//
// The minimum-minutes footnote is a MARIN decision and never a hard-coded
// rule (UI-SPEC §2): it renders ONLY when `minutesShareLabel` is passed
// (e.g. "⅓"), through the translatable template
// "Ranked players: min. {share} of possible minutes".
import React, { useState } from "react";
import { ratingTier10 } from "../totw/totwUtils";
import {
  CompetitionCrest,
  CompetitionFlag,
  PlayerPhoto,
} from "./shared/CompetitionMedia";
import { C, FONT_FAMILY, formatMinutes, formatRating2 } from "./shared/competitionsUi";
import useCompetitionsTr from "./shared/useCompetitionsTr";
import useContainerWidth from "./shared/useContainerWidth";

const RatingChip = ({ rating, compact = false }) => {
  const text = formatRating2(rating);
  if (text == null) return null; // provider artefact -> no chip, never "0.00"
  const tier = ratingTier10(rating);
  return (
    <span
      className="notranslate"
      style={{
        minWidth: compact ? 44 : 52,
        textAlign: "center",
        padding: compact ? "4px 6px" : "5px 8px",
        borderRadius: 2,
        fontSize: compact ? 13 : 13.5,
        fontWeight: 700,
        color: tier.fg,
        background: tier.bg,
        fontVariantNumeric: "tabular-nums",
        display: "inline-block",
      }}
    >
      {text}
    </span>
  );
};

const rowInteractivity = (row, onNavigate, navigable) => {
  if (!onNavigate || !row.player || row.player.id == null) return {};
  // Per-row affordance gate (TOTW pattern: no dead cursor on rows the page
  // cannot resolve to a profile).
  if (
    typeof navigable === "function" &&
    !navigable({ type: "player", id: row.player.id, row })
  ) {
    return {};
  }
  return {
    role: "button",
    tabIndex: 0,
    onClick: () => onNavigate({ type: "player", id: row.player.id, row }),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onNavigate({ type: "player", id: row.player.id, row });
      }
    },
    style: { cursor: "pointer" },
  };
};

const headCellStyle = {
  fontSize: 11.5,
  fontWeight: 600,
  color: C.ink500,
  textAlign: "right",
  whiteSpace: "nowrap",
};

const CompetitionRatingBoard = ({
  rows = [],
  minutesShareLabel,
  visibleCount = 10,
  expandedCount = 25,
  renderAction,
  onNavigate,
  navigable,
  title,
}) => {
  const { tr } = useCompetitionsTr();
  const [containerRef, width] = useContainerWidth(1024);
  const [expanded, setExpanded] = useState(false);
  const compact = width < 560;

  if (!rows.length) return null;

  const shown = expanded ? rows.slice(0, expandedCount) : rows.slice(0, visibleCount);
  const canExpand = !expanded && rows.length > visibleCount;
  const footnote = minutesShareLabel
    ? tr("Ranked players: min. {share} of possible minutes").replace(
        "{share}",
        String(minutesShareLabel),
      )
    : null;

  const desktopGrid = renderAction
    ? "44px minmax(0,1fr) 60px 80px 84px auto"
    : "44px minmax(0,1fr) 60px 80px 84px";
  const mobileGrid = renderAction
    ? "20px minmax(0,1fr) 48px auto"
    : "20px minmax(0,1fr) 48px";

  return (
    <div
      ref={containerRef}
      style={{
        fontFamily: FONT_FAMILY,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 10,
          padding: compact ? "14px 16px" : "16px 20px",
          borderBottom: `1px solid ${C.grid}`,
        }}
      >
        <span style={{ fontSize: compact ? 14.5 : 15.5, fontWeight: 700, color: C.ink900 }}>
          {title || tr("Ø Rating")}
        </span>
      </div>
      {!compact && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: desktopGrid,
            alignItems: "center",
            gap: 14,
            padding: "0 20px",
            background: C.tableHeadBg,
            minHeight: 38,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <span style={headCellStyle}>#</span>
          <span style={{ ...headCellStyle, textAlign: "left" }}>{tr("Player")}</span>
          <span style={headCellStyle}>{tr("Sp")}</span>
          <span style={headCellStyle}>{tr("Min")}</span>
          <span style={headCellStyle}>{tr("Ø Rating")}</span>
          {renderAction && <span />}
        </div>
      )}
      <div>
        {shown.map((row, index) => {
          const interactive = rowInteractivity(row, onNavigate, navigable);
          const minutes = formatMinutes(row.minutes);
          const subline = compact
            ? [
                row.club && row.club.name,
                row.player && row.player.position,
                minutes != null ? `${minutes}′` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : null;
          return (
            <div
              key={(row.player && row.player.id) || `${row.rank}-${index}`}
              {...interactive}
              style={{
                display: "grid",
                gridTemplateColumns: compact ? mobileGrid : desktopGrid,
                alignItems: "center",
                gap: compact ? 10 : 14,
                padding: compact ? "0 14px" : "0 20px",
                minHeight: 52,
                borderBottom: `1px solid ${C.rowDivider}`,
                ...(interactive.style || {}),
              }}
            >
              <span
                style={{
                  fontSize: 13.5,
                  color: C.ink500,
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "right",
                }}
              >
                {row.rank}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <PlayerPhoto
                  name={row.player && row.player.name}
                  photo={row.player && row.player.photo}
                  size={compact ? 28 : 32}
                />
                <span
                  style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: 14.5,
                        fontWeight: 500,
                        color: C.ink900,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row.player && row.player.name}
                    </span>
                    <CompetitionFlag
                      country={
                        row.player && (row.player.nationality || row.player.countryCode)
                      }
                      size={11}
                    />
                    {!compact && (
                      <CompetitionCrest
                        name={row.club && row.club.name}
                        logo={row.club && row.club.logo}
                        size={20}
                      />
                    )}
                    {!compact && row.player && row.player.position && (
                      <span style={{ fontSize: 12, color: C.ink500, whiteSpace: "nowrap" }}>
                        {row.player.position}
                      </span>
                    )}
                  </span>
                  {subline && (
                    <span
                      style={{
                        fontSize: 11.5,
                        color: C.ink500,
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {subline}
                    </span>
                  )}
                </span>
              </span>
              {!compact && (
                <span
                  style={{
                    fontSize: 13.5,
                    color: C.ink700,
                    fontVariantNumeric: "tabular-nums",
                    textAlign: "right",
                  }}
                >
                  {row.matches != null ? row.matches : ""}
                </span>
              )}
              {!compact && (
                <span
                  style={{
                    fontSize: 13.5,
                    color: C.ink700,
                    fontVariantNumeric: "tabular-nums",
                    textAlign: "right",
                  }}
                >
                  {minutes != null ? minutes : ""}
                </span>
              )}
              <span style={{ textAlign: "right" }}>
                <RatingChip rating={row.rating} compact={compact} />
              </span>
              {renderAction && (
                <span style={{ display: "inline-flex", justifyContent: "flex-end" }}>
                  {renderAction(row)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {(footnote || canExpand) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: footnote ? "space-between" : "flex-end",
            gap: 10,
            padding: compact ? "12px 14px" : "13px 20px",
          }}
        >
          {footnote && <span style={{ fontSize: 12, color: C.ink500 }}>{footnote}</span>}
          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: 13,
                fontWeight: 600,
                color: C.accent,
                cursor: "pointer",
                fontFamily: FONT_FAMILY,
              }}
            >
              {tr("Show 25")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CompetitionRatingBoard;
